import { memo, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { useStewardStore, type Message } from '@/stores/useStewardStore'
import { Markdown } from './Markdown'
import { JobCard } from './JobCard'
import { FileCard } from './FileCard'
import { NoteCard } from './NoteCard'
import { StewardTimeline } from './StewardTimeline'

interface Props {
  message: Message
}

/**
 * One settled conversation turn. Visual language matches the concierge chat:
 *   user      → right-aligned dark bubble, plain text
 *   assistant → left-aligned, no bubble, markdown-rendered
 *   card      → left-aligned rich block (Job brief / file preview)
 * 15px body throughout.
 */
function StewardMessageInner({ message }: Props) {
  const appendFileCard = useStewardStore((s) => s.appendFileCard)

  // Stable renderer identity so Markdown's `useMemo([renderCard])` holds — otherwise a fresh
  // inline function each render rebuilds react-markdown's `components`, remounting the inline
  // JobCard, which re-fetches and flashes "Loading the brief…" on every keystroke once a
  // finished-job card is in the transcript.
  const renderCard = useCallback(
    (path: string) => (
      <div className="my-2">
        <JobCard path={path} onOpenFile={appendFileCard} />
      </div>
    ),
    [appendFileCard],
  )

  // Card messages render a rich inline block instead of text.
  if (message.card) {
    const card = message.card
    return (
      <div className="flex justify-start">
        {card.kind === 'job' ? (
          <JobCard path={card.path} onOpenFile={appendFileCard} />
        ) : card.kind === 'note' ? (
          <NoteCard noteKind={card.noteKind} id={card.id} title={card.title} onOpenFile={appendFileCard} />
        ) : (
          <FileCard path={card.path} name={card.name} ext={card.ext} />
        )}
      </div>
    )
  }

  const isUser = message.role === 'user'
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] text-[15px] leading-relaxed',
          isUser
            ? 'whitespace-pre-wrap rounded-2xl bg-[rgb(28,28,30)] px-3.5 py-2.5 text-white'
            : 'py-1 text-foreground'
        )}
      >
        {isUser ? (
          message.content
        ) : (
          <>
            {/* The folded per-node timeline that produced this turn (if any). */}
            {message.steps && message.steps.length > 0 && (
              <StewardTimeline steps={message.steps} />
            )}
            <Markdown source={message.content} className="steward-prose" renderCard={renderCard} />
          </>
        )}
      </div>
    </div>
  )
}

// Settled turns keep a stable `message` prop, so memo stops them re-rendering (and re-parsing
// their markdown) when the user types in the draft input — the keystroke flash.
export const StewardMessage = memo(StewardMessageInner)
